import { css } from "@emotion/css";
import { keyframes } from "@emotion/react";
import { useMemo } from "react";
import { mergeProps } from "react-aria";
import {
  ModalOverlay as AriaModalOverlay,
  ModalOverlayProps,
} from "react-aria-components";

import { mq } from "../../utils";

interface Props extends ModalOverlayProps {
  scroll?: "viewport" | "body";
}

export function _ModalOverlay(props: Props) {
  const { scroll = "body" } = props;

  const className = useMemo(() => {
    return css(
      styles.modalOverlay,
      scroll === "viewport" && styles.viewportScroll,
      scroll === "body" && styles.bodyScroll
    );
  }, [scroll]);

  return <AriaModalOverlay {...mergeProps(props, { className })} />;
}

const fade = keyframes`
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
`;

const styles = {
  modalOverlay: css({
    height: "var(--visual-viewport-height)",
    left: 0,
    position: "fixed",
    top: 0,
    width: "100vw",
    form: { height: "100%" },

    "&[data-entering]": {
      animation: `${fade} 100ms`,
    },

    "&[data-exiting]": {
      animation: `${fade} 100ms reverse ease-in`,
    },
    background: "var(--surface-modal)",
  }),
  viewportScroll: css(
    {
      display: "flex",
      overflow: "scroll",
      flexDirection: "column",
      ".react-aria-Modal": css(
        {
          marginLeft: "auto",
          marginRight: "auto",
        },
        mq({
          marginTop: ["auto"],
          marginBottom: [0, "auto"],
        })
      ),
    },
    mq({
      justifyItems: ["flex-end", "center"],
    })
  ),
  bodyScroll: css(
    {
      display: "flex",
      ".react-aria-Modal": css(
        mq({
          marginTop: [0, "calc(-1 * var(--space-10))"],
          maxWidth: [
            "100vw",
            "calc(100vw - var(--space-10) - var(--space-10))",
          ],
          maxHeight: [
            "calc(var(--visual-viewport-height) - var(--space-1))",
            "calc(var(--visual-viewport-height) - var(--space-10) - var(--space-10))",
          ],
        })
      ),
      ".react-aria-DialogBody": {
        flex: 1,
        overflow: "auto",
      },
    },
    mq({
      alignItems: ["flex-end", "center"],
      justifyContent: ["stretch", "center"],
    })
  ),
};
